import * as React from 'react';
import { snap, story, visualize } from 'storybook-snapper';
import { Accordion, AccordionSize, AccordionTheme } from '..';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import { classes } from './Accordion.visual.st.css';
import { ArrowTopRight, Minus } from '@wix/wix-ui-icons-common/on-stage';
import { AccordionDriver, accordionDriverFactory } from './Accordion.driver';
import { VisualAsync } from '../../test/visual/VisualAsync';

const renderStandardItems = (options?: { itemDisabled?: boolean }) => {
  const isDisabled = options?.itemDisabled;
  return [
    <Accordion.Item
      key="1"
      id="1"
      title="First Item Title"
      subtitle="First Item Subtitle"
      disabled={isDisabled}
    >
      I'm a paragraph. Click here to add your own text and edit me. Let your
      users get to know you.
    </Accordion.Item>,
    <Accordion.Item
      key="2"
      id="2"
      title="Second Item Title"
      subtitle="Second Item Subtitle"
    >
      I'm a paragraph. Click here to add your own text and edit me. Let your
      users get to know you.
    </Accordion.Item>,
  ];
};

visualize('Accordion', () => {
  story('simple / focus', () => {
    const its = [
      {
        it: 'first item, without initially expanded item',
        props: {},
        action: async (driver: AccordionDriver) => driver.toggleItemAt(0),
      },
      {
        it: 'second item, without initially expanded item',
        props: {},
        action: async (driver: AccordionDriver) => driver.toggleItemAt(1),
      },
      {
        it: 'first item, with second item initially expanded',
        props: { initiallyExpanded: '2' },
        action: async (driver: AccordionDriver) => driver.toggleItemAt(0),
      },
      {
        it: 'second item, with second item initially expanded',
        props: { initiallyExpanded: '2' },
        action: async (driver: AccordionDriver) => driver.toggleItemAt(1),
      },
    ];

    its.forEach(({ it, props, action }) =>
      snap(it, (done) => (
        <VisualAsync
          driverFactory={accordionDriverFactory}
          onDone={done}
          action={action}
        >
          <Accordion {...props}>
            {[
              ...renderStandardItems(),
              <Accordion.Item
                key="3"
                id="3"
                title="Third Item Title"
                subtitle="Third Item Subtitle"
              >
                <div>
                  I'm a paragraph. Click here to add your own text and edit me.
                  Let your users get to know you.
                </div>
                <div>
                  <button>some button</button>
                </div>
              </Accordion.Item>,
            ]}
          </Accordion>
        </VisualAsync>
      )),
    );
  });

  story('simple', () => {
    snap(
      'default props',
      <Accordion initiallyExpanded="1">{renderStandardItems()}</Accordion>,
    );

    snap(
      'With multiple prop',
      <Accordion multiple initiallyExpanded={['1', '2']}>
        {[
          ...renderStandardItems(),
          <Accordion.Item
            key="3"
            id="3"
            title="Third Item Title"
            subtitle="Third Item Subtitle"
          >
            I'm a paragraph. Click here to add your own text and edit me. Let
            your users get to know you.
          </Accordion.Item>,
        ]}
      </Accordion>,
    );

    snap(
      'With disabled item',
      <Accordion multiple initiallyExpanded={['1', '2']}>
        <Accordion.Item
          id="1"
          title="First Item Title"
          subtitle="First Item Subtitle"
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
        <Accordion.Item
          id="2"
          title="Second Item Title"
          subtitle="Second Item Subtitle"
          disabled
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
      </Accordion>,
    );

    snap(
      'With suffix',
      <Accordion initiallyExpanded="1">
        <Accordion.Item
          id="1"
          title="Item with a suffix"
          suffix={<div>Suffix</div>}
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
        <Accordion.Item
          id="2"
          title="Item with a suffix"
          suffix={<div>Suffix 2</div>}
          subtitle="A subtitle"
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
        <Accordion.Item id="3" title="Item without suffix">
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
      </Accordion>,
    );

    snap(
      'Large size',
      <Accordion initiallyExpanded="1">
        <Accordion.Item
          id="1"
          title="Item with a suffix"
          suffix={<div>Suffix</div>}
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
        <Accordion.Item
          id="2"
          title="Item with a suffix"
          suffix={<div>Suffix</div>}
          subtitle="A subtitle"
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
      </Accordion>,
    );

    snap(
      'Theme box',
      <Accordion theme={AccordionTheme.Box} initiallyExpanded="1">
        <Accordion.Item
          id="1"
          title="Item with a suffix"
          suffix={<div>Suffix</div>}
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
        <Accordion.Item
          id="2"
          title="Item with a suffix"
          suffix={<div>Suffix</div>}
          subtitle="A subtitle"
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
      </Accordion>,
    );

    snap(
      'Icon selection',
      <Accordion
        icons={{ expanded: Minus, collapsed: ArrowTopRight }}
        initiallyExpanded="1"
      >
        <Accordion.Item
          id="1"
          title="Item with a suffix"
          suffix={<div>Suffix</div>}
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
        <Accordion.Item
          id="2"
          title="Item with a suffix"
          suffix={<div>Suffix</div>}
          subtitle="A subtitle"
        >
          I'm a paragraph. Click here to add your own text and edit me. Let your
          users get to know you.
        </Accordion.Item>
      </Accordion>,
    );

    snap(
      'mobile',
      <TPAComponentsProvider value={{ mobile: true }}>
        <Accordion initiallyExpanded="1">{renderStandardItems()}</Accordion>
      </TPAComponentsProvider>,
    );
    snap(
      'rtl',
      <div dir="rtl">
        <TPAComponentsProvider value={{ rtl: true }}>
          <Accordion initiallyExpanded="1">{renderStandardItems()}</Accordion>
        </TPAComponentsProvider>
      </div>,
    );

    snap(
      'background overrides',
      <Accordion className={classes.backgroundOverrides} initiallyExpanded="1">
        {renderStandardItems()}
      </Accordion>,
    );

    snap(
      'item text overrides',
      <Accordion className={classes.itemTextOverrides} initiallyExpanded="1">
        {renderStandardItems()}
      </Accordion>,
    );
    snap(
      'item wst overrides',
      (() => {
        const wstOverrides = {
          '--wst-shade-3-color-rgb': '255, 0, 0',
          '--wst-primary-background-color-rgb': '180, 180, 255',
          '--wst-paragraph-2-color-rgb': '20, 100, 20',
        } as React.CSSProperties;
        return (
          <div style={wstOverrides}>
            <Accordion initiallyExpanded="1">{renderStandardItems()}</Accordion>
          </div>
        );
      })(),
    );
    snap(
      'item wst font',
      (() => {
        const wstFont = {
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-paragraph-2-line-height': '22px',
          '--wst-paragraph-2-font-weight': '900',
          '--wst-paragraph-2-font-style': 'italic',
          '--wst-paragraph-2-font-variant': 'small-caps',
        } as React.CSSProperties;
        return (
          <div style={wstFont}>
            <Accordion initiallyExpanded="1">{renderStandardItems()}</Accordion>
          </div>
        );
      })(),
    );
    snap(
      'border radius overrides',
      <Accordion
        className={classes.borderRadiusOverrides}
        initiallyExpanded="1"
        theme={AccordionTheme.Box}
      >
        {renderStandardItems()}
      </Accordion>,
    );
    snap(
      'icon size overrides',
      <Accordion className={classes.iconSizeOverrides} initiallyExpanded="1">
        {renderStandardItems()}
      </Accordion>,
    );
    snap(
      'disabled item overrides',
      <Accordion
        className={classes.disabledItemOverrides}
        initiallyExpanded="1"
      >
        {renderStandardItems({ itemDisabled: true })}
      </Accordion>,
    );

    snap(
      'divider overrides',
      <Accordion
        className={classes.dividerOverrides}
        multiple
        initiallyExpanded={['1', '2']}
      >
        {[
          ...renderStandardItems(),
          <Accordion.Item
            key="3"
            id="3"
            title="Third Item Title"
            subtitle="Third Item Subtitle"
          >
            I'm a paragraph. Click here to add your own text and edit me. Let
            your users get to know you.
          </Accordion.Item>,
        ]}
      </Accordion>,
    );

    [
      { size: AccordionSize.Small, theme: AccordionTheme.Box },
      { size: AccordionSize.Large, theme: AccordionTheme.Box },
      { size: AccordionSize.Small, theme: AccordionTheme.Line },
      { size: AccordionSize.Large, theme: AccordionTheme.Line },
    ].forEach(({ size, theme }) =>
      snap(
        `padding overrides ${theme} theme ${size} size `,
        <div className={classes.paddingsOverrides}>
          <Accordion theme={theme} initiallyExpanded="1" size={size}>
            <Accordion.Item
              id="1"
              title="Item with overridden padding"
              subtitle="Custom padding subtitle"
            >
              Content with padding overrides. I'm a paragraph. Click here to add
              your own text and edit me. Let your users get to know you. I'm a
              paragraph. Click here to add your own text and edit me. Let your
              users get to know you.
            </Accordion.Item>
            <Accordion.Item id="2" title="Another item" subtitle="Subtitle">
              More content with overridden padding.
            </Accordion.Item>
          </Accordion>
        </div>,
      ),
    );

    snap(
      'Header alignment override',
      <Accordion initiallyExpanded="1" className={classes.alignOverrides}>
        <Accordion.Item id="1" title="Centered Header">
          Content goes here
        </Accordion.Item>
      </Accordion>,
    );
  });
});
